/**
 * HustleEventDetailPage — public event detail + seat picker + checkout.
 *
 * Handles ALL three seating models the platform supports:
 *   • general — one Buy button, choose quantity
 *   • seated  — SVG grid of sections × rows × seats
 *   • tables  — round-table icons, pick one seat per table
 *
 * The seat-pick flow is:
 *   1. User taps seats → local selection state.
 *   2. Click "Continue" → POST /api/hustle-events/{id}/seat-hold
 *      (5-minute TTL server-side).
 *   3. On success → POST /api/hustle-events/{id}/checkout with the
 *      hold_id → Stripe redirect. Success returns to
 *      /hustle-events/success and the backend webhook does the rest.
 *
 * Global chrome: PlatformHeader + SuiteFooter with i18n-safe strings.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Ticket, MapPin, Calendar as CalendarIcon, X } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/** One rectangular seat in the SVG grid. */
function SeatRect({ x, y, size, seat, selected, onToggle }) {
  const status = seat.status;
  const isAvailable = status === "available";
  const fill = selected
    ? "#f59e0b"
    : status === "sold" || status === "held"
        ? "#4b5563"
        : "#e5e7eb";
  return (
    <rect
      x={x} y={y}
      width={size - 4} height={size - 4} rx={4}
      fill={fill}
      stroke={selected ? "#b45309" : "transparent"}
      strokeWidth={selected ? 2 : 0}
      style={{ cursor: isAvailable ? "pointer" : "not-allowed" }}
      onClick={() => isAvailable && onToggle(seat)}
      data-testid={`seat-${seat.row}-${seat.seat}`}
    />
  );
}

function SeatedSectionMap({ section, selected, toggle }) {
  const rows = section.rows || [];
  const spr = section.seats_per_row || 0;
  const size = 26;
  const width = spr * size + 60;
  const height = rows.length * size + 30;
  return (
    <div
      data-testid={`section-${section.code}`}
      className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400">
            {section.is_vip ? "VIP" : "Section"}
          </p>
          <h4 className="font-black text-lg">
            {section.code} · {section.label}
          </h4>
        </div>
        <p className="text-amber-500 font-black">
          ${(section.price_cents / 100).toFixed(2)}
        </p>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
        {rows.map((rowLabel, ri) => (
          <g key={rowLabel}>
            <text
              x={20} y={ri * size + size / 2 + 12}
              className="fill-stone-400"
              fontSize="10"
            >
              {rowLabel}
            </text>
            {Array.from({ length: spr }).map((_, si) => {
              const seat = section.seats.find(
                (s) => s.row === String(rowLabel) && s.seat === String(si + 1),
              );
              if (!seat) return null;
              const key = `${section.code}-${rowLabel}-${si + 1}`;
              const sel = selected.has(key);
              return (
                <SeatRect
                  key={key}
                  x={40 + si * size} y={ri * size + 4}
                  size={size} seat={seat} selected={sel}
                  onToggle={() =>
                    toggle({
                      section: section.code,
                      row: rowLabel,
                      seat: String(si + 1),
                      price_cents: section.price_cents,
                    })
                  }
                />
              );
            })}
          </g>
        ))}
      </svg>
    </div>
  );
}

function TableGrid({ table, selected, toggle }) {
  return (
    <div
      data-testid={`table-${table.code}`}
      className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400">
            {table.is_vip ? "VIP Table" : "Table"}
          </p>
          <h4 className="font-black text-lg">
            {table.code} · {table.label}
          </h4>
        </div>
        <p className="text-amber-500 font-black">
          ${(table.price_per_seat_cents / 100).toFixed(2)}/seat
        </p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {table.seats.map((s) => {
          const key = `T${table.code}-${s.seat_at_table}`;
          const sel = selected.has(key);
          const avail = s.status === "available";
          return (
            <button
              key={key}
              type="button"
              disabled={!avail}
              onClick={() =>
                toggle({
                  table: table.code,
                  seat_at_table: s.seat_at_table,
                  price_cents: table.price_per_seat_cents,
                })
              }
              data-testid={`table-seat-${table.code}-${s.seat_at_table}`}
              className={`h-9 rounded-full text-xs font-bold transition ${
                sel
                  ? "bg-amber-400 text-slate-950"
                  : !avail
                      ? "bg-stone-700/40 text-stone-500 cursor-not-allowed"
                      : "bg-stone-200 dark:bg-stone-800 hover:bg-amber-200"
              }`}
            >
              {s.seat_at_table}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function HustleEventDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Map()); // key → pick
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [applyCredit, setApplyCredit] = useState(false);

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hustle-events/${slug}`);
        setEv(data);
        document.title = `${data.title} — HustleEvents`;
        // Best-effort credit balance lookup — only shows when the buyer
        // is signed in AND has credit at this vendor.
        const vendorId = data.vendor_id || data.promoter_id || data.id;
        try {
          const cb = await axios.get(
            `${API}/buyer-credits/vendor/${vendorId}`,
            { headers: {
                ...(localStorage.getItem("token")
                    ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
                    : {}) },
              withCredentials: true });
          setCreditBalance(cb.data?.cents_available || 0);
        } catch { /* not logged in — silent */ }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [slug, i18n.language]);

  const selectedKeys = useMemo(() => new Set(selected.keys()), [selected]);
  const total = useMemo(() =>
    [...selected.values()].reduce((n, p) => n + (p.price_cents || 0), 0),
    [selected]);

  const toggle = (pick) => {
    const k = pick.section
      ? `${pick.section}-${pick.row}-${pick.seat}`
      : `T${pick.table}-${pick.seat_at_table}`;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(k)) next.delete(k);
      else next.set(k, pick);
      return next;
    });
  };

  const startCheckout = async () => {
    if (!ev) return;
    if (!buyerName.trim() || !buyerEmail.trim()) {
      toast.error(t("events_err_details", { defaultValue: "Enter your name and email first." }));
      return;
    }
    if (ev.seating_mode !== "general" && selected.size === 0) {
      toast.error(t("events_err_pick_seat", { defaultValue: "Pick at least one seat." }));
      return;
    }
    setBusy(true);
    try {
      let seatsForServer;
      if (ev.seating_mode === "general") {
        seatsForServer = [{ general: true }];
      } else {
        seatsForServer = [...selected.values()].map(({ section, row, seat, table, seat_at_table }) =>
          section
            ? { section, row, seat }
            : { table, seat_at_table }
        );
      }
      const holdRes = await axios.post(
        `${API}/hustle-events/${ev.id}/seat-hold`,
        { seats: seatsForServer },
        { withCredentials: true },
      );
      const { hold_id } = holdRes.data;
      const checkoutRes = await axios.post(
        `${API}/hustle-events/${ev.id}/checkout`,
        {
          hold_id,
          origin_url: window.location.origin,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          apply_credit_cents: applyCredit ? creditBalance : 0,
        },
        { withCredentials: true },
      );
      if (checkoutRes.data?.session_url) {
        window.location.href = checkoutRes.data.session_url;
      } else {
        toast.error(t("events_err_checkout", { defaultValue: "Checkout failed. Try again." }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="hustle-event-detail-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleEvents", to: "/hustle-events" }}
        nav={[{ to: "/hustle-events", label: t("events_nav_catalog", { defaultValue: "Events" }) }]}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        {!ev && !error && (
          <div data-testid="event-loading">
            {t("events_detail_loading", { defaultValue: "Loading event…" })}
          </div>
        )}
        {error && (
          <div data-testid="event-error" className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3">
            {error}
          </div>
        )}
        {ev && (
          <>
            <header className="mb-8">
              <button type="button"
                onClick={() => navigate("/hustle-events")}
                className="text-sm text-stone-500 hover:text-amber-500 mb-3"
                data-testid="event-back"
              >
                ← {t("events_detail_back", { defaultValue: "All events" })}
              </button>
              <h1 className="text-3xl md:text-5xl font-black leading-tight">
                {ev.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-stone-500 dark:text-stone-300">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarIcon size={14} className="text-amber-500" /> {fmtWhen(ev.starts_at)}
                </span>
                {ev.venue_name && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={14} className="text-amber-500" /> {ev.venue_name}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 uppercase tracking-widest text-[10px] bg-stone-200 dark:bg-stone-800 rounded-full px-3 py-1">
                  {ev.seating_mode}
                </span>
              </div>
              {ev.description && (
                <p className="mt-4 max-w-3xl text-stone-500 dark:text-stone-300 whitespace-pre-line">
                  {ev.description}
                </p>
              )}
            </header>

            {ev.menu_url && (
              <section
                data-testid="event-vendor-menu"
                className="mb-8 rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 overflow-hidden"
              >
                <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 dark:border-stone-800">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-amber-500 font-black">
                      Tonight&apos;s menu
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Straight from the venue — scroll to preview or open in a new tab.
                    </p>
                  </div>
                  <a
                    href={ev.menu_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="event-menu-open"
                    className="text-xs font-black text-amber-500 hover:text-amber-400 whitespace-nowrap"
                  >
                    Open menu ↗
                  </a>
                </div>
                <iframe
                  src={ev.menu_url}
                  title="Vendor menu"
                  loading="lazy"
                  className="w-full h-[520px] bg-white"
                  data-testid="event-menu-iframe"
                />
              </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
              <div>
                {ev.seating_mode === "seated" && (
                  <div data-testid="seated-map" className="space-y-6">
                    {(ev.sections || []).map((sec) => (
                      <SeatedSectionMap key={sec.code}
                        section={sec}
                        selected={selectedKeys}
                        toggle={toggle}
                      />
                    ))}
                  </div>
                )}
                {ev.seating_mode === "tables" && (
                  <div data-testid="tables-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(ev.tables || []).map((tb) => (
                      <TableGrid key={tb.code}
                        table={tb}
                        selected={selectedKeys}
                        toggle={toggle}
                      />
                    ))}
                  </div>
                )}
                {ev.seating_mode === "general" && (
                  <div
                    data-testid="general-admission"
                    className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 text-center"
                  >
                    <Ticket size={32} className="mx-auto text-amber-500" />
                    <p className="mt-3 font-black text-2xl">
                      ${((ev.general_price_cents || 0) / 100).toFixed(2)}
                    </p>
                    <p className="text-sm text-stone-500 mt-1">
                      {t("events_ga_note", { defaultValue: "General admission — one ticket per checkout." })}
                    </p>
                  </div>
                )}
              </div>

              <aside
                data-testid="event-checkout-card"
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 sticky top-24 h-fit"
              >
                <h3 className="font-black text-lg mb-4">
                  {t("events_checkout_title", { defaultValue: "Your seats" })}
                </h3>
                {ev.seating_mode !== "general" && selected.size === 0 && (
                  <p className="text-sm text-stone-400" data-testid="checkout-empty">
                    {t("events_checkout_empty", { defaultValue: "Tap a seat to reserve it." })}
                  </p>
                )}
                {[...selected.entries()].map(([k, p]) => (
                  <div key={k} className="flex items-center justify-between text-sm py-1">
                    <span>{k}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-amber-500 font-bold">
                        ${(p.price_cents / 100).toFixed(2)}
                      </span>
                      <button type="button"
                        onClick={() => toggle(p)}
                        aria-label="remove"
                        data-testid={`remove-seat-${k}`}
                      >
                        <X size={14} className="text-stone-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <input
                    type="text"
                    placeholder={t("events_field_name", { defaultValue: "Full name" })}
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    data-testid="checkout-name"
                    className="h-10 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
                  />
                  <input
                    type="email"
                    placeholder={t("events_field_email", { defaultValue: "Email for tickets" })}
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    data-testid="checkout-email"
                    className="h-10 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
                  />
                </div>
                <div className="mt-5 border-t border-stone-200 dark:border-stone-800 pt-4 flex items-center justify-between">
                  <span className="uppercase tracking-widest text-[11px] text-stone-500">
                    {t("events_checkout_total", { defaultValue: "Total" })}
                  </span>
                  <span data-testid="checkout-total"
                    className="font-black text-xl">
                    ${(
                      (ev.seating_mode === "general"
                        ? (ev.general_price_cents || 0)
                        : total) / 100
                    ).toFixed(2)}
                  </span>
                </div>
                {creditBalance > 0 && (
                  <label
                    data-testid="event-apply-credit"
                    className="mt-3 flex items-center gap-2 text-xs bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 rounded-lg px-3 py-2 cursor-pointer"
                  >
                    <input type="checkbox"
                      checked={applyCredit}
                      onChange={(e) => setApplyCredit(e.target.checked)}/>
                    <span className="text-violet-700 dark:text-violet-300">
                      Apply <strong>${(creditBalance/100).toFixed(2)}</strong> credit at this vendor
                    </span>
                  </label>
                )}
                <Button
                  className="mt-4 w-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-full h-11"
                  disabled={busy}
                  onClick={startCheckout}
                  data-testid="checkout-submit"
                >
                  {busy
                    ? t("events_checkout_busy", { defaultValue: "Reserving…" })
                    : t("events_checkout_go", { defaultValue: "Continue to payment" })}
                </Button>
                <p className="mt-3 text-[10px] text-stone-400 text-center">
                  {t("events_checkout_hold_note", {
                    defaultValue: "Seats are held for 5 minutes while you pay.",
                  })}
                </p>
              </aside>
            </div>
          </>
        )}
      </div>
      <SuiteFooter trivagoContext="events" />
    </div>
  );
}
